import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import '../news.css';
import AdminHeader from "../../../Components/AdminHeader/AdminHeader";
import AdminNav from "../../../Components/AdminNav/AdminNav";

import AddImageComponent from "./AddImageComponent";
import AddCustomBodyComponent from "./AddCustomBodyComponent";
import Add4ImageGallery from "./Add4ImageGallery";
import AddGalleryComponent from "./AddGalleryComponent";
import ImageText from "./ImageText";
import TextImage from "./TextImage";
import TableComponent from "./TableComponent";
import QuoteText from "./QuoteText";
import TextQuote from "./TextQuote"; // ✅ NEW IMPORT
import { createCustomNews } from '../../../actions/NewsPages';

interface FormDataState {
    title: string;
    subtitle: string;
    year_posted: string;
    image: File | null;
}

interface SectionData {
    id: number;
    type:
        | 'image'
        | 'body'
        | 'gallery4'
        | 'gallery'
        | 'imageText'
        | 'textImage'
        | 'table'
        | 'quote'
        | 'textQuote'; // ✅ NEW TYPE

    bodyCopy?: string;
    quote?: string;
    tableData?: string[];
    imageFile?: File | null;
    caption?: string;
    isFullWidth?: boolean;
    images?: { id: number; file?: File | null; caption?: string }[];
    title?: string;
}

function CreateCustomArticle() {
    const location = useLocation();
    const navigate = useNavigate();

    if (!location.state?.user) navigate('/login');

    const user = location.state?.user;
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [formData, setFormData] = useState<FormDataState>({
        title: "",
        subtitle: "",
        year_posted: "",
        image: null
    });
    const [dynamicSections, setDynamicSections] = useState<SectionData[]>([]);

    const updateSection = (id: number, updatedData: Partial<SectionData>) => {
        setDynamicSections(prev =>
            prev.map(section => (section.id === id ? { ...section, ...updatedData } : section))
        );
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        if (type === "file") {
            const fileInput = e.target as HTMLInputElement;
            setFormData(prev => ({ ...prev, [name]: fileInput.files?.[0] || null }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const addSection = (type: SectionData['type']) => {
        const baseId = Date.now();
        if (type === 'gallery4') {
            setDynamicSections(prev => [
                ...prev,
                { id: baseId, type, images: Array(4).fill(null).map((_, i) => ({ id: baseId + i, file: null })) }
            ]);
        } else if (type === 'gallery') {
            setDynamicSections(prev => [
                ...prev,
                { id: baseId, type, title: '', images: [{ id: baseId, file: null, caption: '' }] }
            ]);
        } else if (type === 'table') {
            setDynamicSections(prev => [
                ...prev,
                { id: baseId, type, tableData: Array(2).fill("") }
            ]);
        } else {
            // ✅ Default for other section types (including quote & textQuote)
            setDynamicSections(prev => [...prev, { id: baseId, type }]);
        }
    };

    const removeSection = (id: number) => {
        setDynamicSections(prev => prev.filter(section => section.id !== id));
    };

    const handleSave = async (isDraft: boolean) => {
        try {
            const data = new FormData();
            data.append('title', formData.title);
            data.append('subtitle', formData.subtitle);
            data.append('year_posted', formData.year_posted);
            data.append('draft', isDraft ? '1' : '0');

            if (formData.image) data.append('image', formData.image);

            dynamicSections.forEach((section, index) => {
                const comp: any = { type: section.type, display_order: index, data: {} };

                if (section.type === 'body') {
                    comp.data.body_copy = section.bodyCopy || '';
                } else if (section.type === 'quote' || section.type === 'textQuote') { // ✅ Both handled here
                    comp.data.quote = section.quote || '';
                    comp.data.body_copy = section.bodyCopy || '';
                } else if (section.type === 'table') {
                    comp.data.body_copy = section.tableData || [];
                } else if (section.type === 'image' && section.imageFile) {
                    comp.data.is_full_width = section.isFullWidth ? 1 : 0;
                    comp.data.caption = section.caption || '';
                    data.append(`components_files[${index}]`, section.imageFile);
                } else if (section.type === 'gallery4' && section.images?.length) {
                    section.images.forEach((img, imgIndex) => {
                        if (img.file) data.append(`components_files[${index}][${imgIndex}]`, img.file);
                    });
                } else if (section.type === 'gallery' && section.images?.length) {
                    comp.data.title = section.title || '';
                    section.images.forEach((img, imgIndex) => {
                        if (img.file) data.append(`components_files[${index}][${imgIndex}]`, img.file);
                    });
                } else if ((section.type === 'imageText' || section.type === 'textImage') && section.imageFile) {
                    comp.data.body_copy = section.bodyCopy || '';
                    comp.data.is_full_width = section.isFullWidth ? 1 : 0;
                    data.append(`components_files[${index}]`, section.imageFile);
                }

                data.append(`components_meta[${index}]`, JSON.stringify(comp));
            });

            const response = await createCustomNews(data);
            if (response.data.success) {
                alert(isDraft ? 'Draft saved!' : 'Article created successfully!');
                navigate('/dashboard/news/list', { state: { user } });
            }
        } catch (error) {
            console.error(error);
            alert('Error saving article');
        }
    };

    return (
        <div>
            <AdminHeader isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} user={user} />
            <AdminNav collapsed={isCollapsed} setCollapsed={setIsCollapsed} user={user} />

            <div className={`main-admin-page ${isCollapsed ? "collapsed" : "expanded"}`}>
                <div className="row">
                    <div className="col-md-12 col-sm-12 col-xs-12">
                        <div className="x_panel">
                            <div className="x_title"><h2>Create Custom Article</h2></div>
                            <div className="x_content">
                                <form style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="form-control" placeholder="Title" />
                                    <textarea name="subtitle" value={formData.subtitle} onChange={handleInputChange} className="form-control" placeholder="Subtitle" />
                                    <input type="date" name="year_posted" value={formData.year_posted} onChange={handleInputChange} className="form-control" />

                                    <div>
                                        <label>Cover Image</label><br />
                                        <input type="file" name="image" className="form-control" onChange={handleInputChange} /><br />
                                        {formData.image && (
                                            <img
                                                src={URL.createObjectURL(formData.image)}
                                                alt="Cover"
                                                style={{ width: '70%', marginTop: '10px', objectFit: 'cover', borderRadius: '5px' }}
                                            />
                                        )}
                                    </div>

                                    {dynamicSections.map(section => {
                                        switch (section.type) {
                                            case 'image':
                                                return <AddImageComponent key={section.id} section={section} onChange={updateSection} onClose={() => removeSection(section.id)} />;
                                            case 'gallery4':
                                                return <Add4ImageGallery key={section.id} section={section} onChange={updateSection} onClose={() => removeSection(section.id)} />;
                                            case 'gallery':
                                                return <AddGalleryComponent key={section.id} section={section} onChange={updateSection} onClose={() => removeSection(section.id)} />;
                                            case 'imageText':
                                                return <ImageText key={section.id} section={section} onChange={updateSection} onClose={() => removeSection(section.id)} />;
                                            case 'textImage':
                                                return <TextImage key={section.id} section={section} onChange={updateSection} onClose={() => removeSection(section.id)} />;
                                            case 'table':
                                                return (
                                                    <TableComponent
                                                        key={section.id}
                                                        sectionId={section.id}
                                                        initialData={section.tableData || []}
                                                        onChange={(id, updatedData) => updateSection(id, { tableData: updatedData.bodyCopy })}
                                                        onClose={() => removeSection(section.id)}
                                                    />
                                                );
                                            case 'quote':
                                                return <QuoteText key={section.id} section={section} onChange={updateSection} onClose={() => removeSection(section.id)} />;
                                            case 'textQuote': // ✅ NEW CASE
                                                return <TextQuote key={section.id} section={section} onChange={updateSection} onClose={() => removeSection(section.id)} />;
                                            default:
                                                return <AddCustomBodyComponent key={section.id} section={section} onChange={updateSection} onClose={() => removeSection(section.id)} />;
                                        }
                                    })}

                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '20px' }}>
                                        <button type="button" className="btn btn-primary" onClick={() => addSection('image')}>Add Image</button>
                                        <button type="button" className="btn btn-secondary" onClick={() => addSection('body')}>Add Body</button>
                                        <button type="button" className="btn btn-warning" onClick={() => addSection('gallery4')}>Add 4-Image Gallery</button>
                                        <button type="button" className="btn btn-info" onClick={() => addSection('gallery')}>Add Gallery</button>
                                        <button type="button" className="btn btn-info" onClick={() => addSection('imageText')}>Add Image then Text</button>
                                        <button type="button" className="btn btn-dark" onClick={() => addSection('textImage')}>Add Text then Image</button>
                                        <button type="button" className="btn btn-secondary" onClick={() => addSection('table')}>Add Table</button>
                                        <button type="button" className="btn btn-success" onClick={() => addSection('quote')}>Add Quote then Text</button>
                                        <button type="button" className="btn btn-outline-success" onClick={() => addSection('textQuote')}>Add Text then Quote</button> {/* ✅ NEW BUTTON */}
                                    </div>

                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                        <button type="button" className="btn btn-info" onClick={() => handleSave(true)}>Save Draft</button>
                                        <button type="button" className="btn btn-success" onClick={() => handleSave(false)}>Submit</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CreateCustomArticle;
